import { useEffect, useMemo, useRef, useState } from "react";

const buildPriceEndpoint = () => {
  const base =
    process.env.REACT_APP_PROXY_URL ||
    `${window.location.origin}/api/binance/price`;

  return (symbol) => {
    try {
      const url = new URL(base, window.location.origin);
      url.searchParams.set("symbol", symbol);
      return url.toString();
    } catch (error) {
      return `${base}?symbol=${encodeURIComponent(symbol)}`;
    }
  };
};

const formatValue = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "-";
  }

  if (value >= 1000) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5
  });
};

const DEFAULT_SYMBOLS = [
  { symbol: "BTCUSDT", label: "BTC/USDT" },
  { symbol: "ETHUSDT", label: "ETH/USDT" },
  { symbol: "EURUSDT", label: "EUR/USDT" },
  { symbol: "GBPUSDT", label: "GBP/USDT" }
];

export const PopularRatesBar = ({ pairs = DEFAULT_SYMBOLS, refreshMs = 60_000 }) => {
  const [rates, setRates] = useState(() =>
    pairs.map((pair) => ({ ...pair, price: null, direction: "steady" }))
  );
  const [error, setError] = useState("");
  const fetchInFlight = useRef(false);
  const buildUrl = useMemo(() => buildPriceEndpoint(), []);

  useEffect(() => {
    const fetchRates = async () => {
      if (fetchInFlight.current) return;
      fetchInFlight.current = true;

      try {
        const responses = await Promise.all(
          pairs.map(async (pair) => {
            const response = await fetch(buildUrl(pair.symbol));
            if (!response.ok) {
              throw new Error(`Failed to get ${pair.symbol}`);
            }
            const data = await response.json();
            return {
              ...pair,
              price: parseFloat(data.price)
            };
          })
        );

        setRates((prev) =>
          responses.map((current) => {
            const previous = prev.find((p) => p.symbol === current.symbol);
            let direction = "steady";
            if (previous && typeof previous.price === "number") {
              if (current.price > previous.price) {
                direction = "up";
              } else if (current.price < previous.price) {
                direction = "down";
              }
            }
            return { ...current, direction };
          })
        );
        setError("");
      } catch (fetchError) {
        console.error("Popular rates fetch failed:", fetchError);
        setError("Unable to load rates");
      } finally {
        fetchInFlight.current = false;
      }
    };

    fetchRates();
    const intervalId = setInterval(fetchRates, refreshMs);

    return () => clearInterval(intervalId);
  }, [pairs, refreshMs, buildUrl]);

  if (error) {
    return <div className="rates-bar rates-bar--error">{error}</div>;
  }

  return (
    <div className="rates-bar">
      {rates.map(({ symbol, label, price, direction }) => (
        <div key={symbol} className="rates-bar__item">
          <div className="rates-bar__label">
            {label}{" "}
            <span
              className={`rates-bar__indicator rates-bar__indicator--${direction}`}
            >
              {direction === "up" ? "▲" : direction === "down" ? "▼" : "■"}
            </span>
          </div>
          <div className="rates-bar__value">{formatValue(price)}</div>
        </div>
      ))}
    </div>
  );
};
